import { useState, useEffect } from "react";
import { API_HOST } from "../constants";
import type { Lesson } from "../types";

// Кеш для уроков
const lessonCache: Record<string, { lesson: Lesson | null; timestamp: number }> = {};
const lessonFetchPromises: Record<string, Promise<Lesson>> = {}; // Защита от параллельных запросов
const CACHE_DURATION = 5 * 60 * 1000; // 5 минут

export const useLesson = (lessonId: string | undefined) => {
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!lessonId) {
      setLesson(null);
      setLoading(false);
      return;
    }

    let isMounted = true;

    const fetchLesson = async () => {
      // Проверяем кеш
      const cached = lessonCache[lessonId];
      const now = Date.now();
      if (cached?.lesson && (now - cached.timestamp) < CACHE_DURATION) {
        if (isMounted) {
          setLesson(cached.lesson);
          setLoading(false);
        }
        return;
      }

      // Если уже есть запрос для этого lessonId, ждем его
      if (lessonFetchPromises[lessonId]) {
        try {
          const cachedLesson = await lessonFetchPromises[lessonId];
          if (isMounted) {
            setLesson(cachedLesson);
            setLoading(false);
          }
          return;
        } catch {
          // Продолжаем с новым запросом
        }
      }

      try {
        if (isMounted) {
          setLoading(true);
        }

        // Создаем промис для загрузки урока
        const fetchPromise = (async () => {
          const response = await fetch(`${API_HOST}/api/content/lessons/${lessonId}`);
          
          if (!response.ok) {
            if (response.status === 404) {
              throw new Error("Урок не найден");
            }
            throw new Error("Не удалось загрузить урок");
          }

          const lessonData = await response.json();
          
          const formattedLesson: Lesson = {
            id: lessonData.id,
            title: lessonData.title,
            summary: lessonData.summary,
            content: lessonData.content || "",
            estimatedTime: lessonData.estimatedTime,
            quiz: lessonData.quiz,
            links: lessonData.links,
          };

          // Обновляем кеш
          lessonCache[lessonId] = { lesson: formattedLesson, timestamp: Date.now() };
          
          return formattedLesson;
        })();

        // Сохраняем промис для предотвращения дублирующихся запросов
        lessonFetchPromises[lessonId] = fetchPromise;

        const formattedLesson = await fetchPromise;

        // Удаляем промис после завершения
        delete lessonFetchPromises[lessonId];

        if (isMounted) {
          setLesson(formattedLesson);
          setError(null);
        }
      } catch (err) {
        delete lessonFetchPromises[lessonId];
        if (isMounted) {
          console.error("Ошибка загрузки урока:", err);
          setError(err instanceof Error ? err.message : "Неизвестная ошибка");
          setLesson(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchLesson();

    return () => {
      isMounted = false;
    };
  }, [lessonId]);

  return { lesson, loading, error };
};
